"use client";

import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Kbd } from "@/components/shadcn/kbd";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from "@/components/shadcn/input-group";
import { cn } from "@/lib/utils";
import type { ActiveApproval } from "./useApproval";
import { resolveApproval } from "./approvalRegistry";
import { Badge } from "@/components/shadcn/badge";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { resolveApprovalGrant, resolveApprovalLabel } from "../toolDisplay";
import { EntityNamesProvider } from "../entityNames";
import { useMcpPrefixMeta } from "../mcpToolIcon";
import { useComposerKeys } from "../composerKeys";

type Choice = "allow" | "always" | "reject";
const CHOICES: Choice[] = ["allow", "always", "reject"];

export function ApprovalComposer({ approval }: { approval: ActiveApproval }) {
  const { pending } = approval;
  const mcpPrefixes = useMcpPrefixMeta();
  const containerRef = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState(0);
  const [rejecting, setRejecting] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setHighlighted(0);
    setRejecting(false);
    setMessage("");
  }, [pending.approvalId]);

  useEffect(() => {
    if (!rejecting) containerRef.current?.focus({ preventScroll: true });
  }, [rejecting, pending.approvalId]);

  const canAlwaysAllow = pending.alwaysAllow;
  const enabled = (index: number): boolean =>
    CHOICES[index] !== "always" || canAlwaysAllow;

  const choose = (choice: Choice): void => {
    if (choice === "allow") approval.approve();
    else if (choice === "always") {
      if (canAlwaysAllow) approval.alwaysAllow();
    } else {
      setHighlighted(2);
      setRejecting(true);
    }
  };

  // Escape never denies: a reflexive dismiss keystroke must not fire a
  // denial. Rejecting takes 3, then Enter.
  const handleKey = (e: KeyboardEvent | ReactKeyboardEvent): boolean => {
    if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return false;
    const index = ["1", "2", "3"].indexOf(e.key);
    if (index >= 0) {
      choose(CHOICES[index]);
      return true;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      const step = e.key === "ArrowDown" ? 1 : -1;
      setHighlighted((i) => {
        let next = (i + step + CHOICES.length) % CHOICES.length;
        while (!enabled(next)) {
          next = (next + step + CHOICES.length) % CHOICES.length;
        }
        return next;
      });
      return true;
    }
    if (e.key === "Enter") {
      choose(CHOICES[highlighted]);
      return true;
    }
    return false;
  };

  useComposerKeys({
    containerRef,
    listening: !rejecting,
    onKey: handleKey,
    isChoiceKey: (key) => ["1", "2", "3"].includes(key),
  });

  const submitRejection = (): void => approval.deny(message);

  const onMessageKey = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setRejecting(false);
    } else if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submitRejection();
    }
  };

  // Same source as the message card, so the two always read identically.
  const label = resolveApprovalLabel(pending.part);
  const rendered = resolveApproval(pending.part);
  const actionSummary = rendered?.summary() ?? null;
  const grant = resolveApprovalGrant(pending.part, mcpPrefixes);

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      data-approval-card
      data-composer-card
      className="outline-none rounded-xl border border-border bg-card shadow-sm overflow-hidden"
    >
      <div className="px-4 pt-3 pb-3">
        <div className="flex items-baseline gap-2">
          <p className="min-w-0 flex-1 text-sm font-medium text-foreground truncate">
            {label}
          </p>
          {pending.total > 1 && (
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
              {pending.position} of {pending.total}
            </span>
          )}
        </div>
        {actionSummary && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {actionSummary}
          </p>
        )}
      </div>

      {rendered?.hasBody && (
        <div className="px-4 pb-3 max-h-52 overflow-y-auto border-t border-border pt-3">
          <EntityNamesProvider part={pending.part}>
            {rendered.body()}
          </EntityNamesProvider>
        </div>
      )}

      <div
        role="group"
        aria-label="Approval choices"
        className="flex flex-col gap-0.5 border-t border-border p-1.5"
      >
        <ChoiceRow
          index={0}
          highlighted={!rejecting && highlighted === 0}
          ariaLabel="Allow"
          onHover={() => setHighlighted(0)}
          onSelect={() => choose("allow")}
        >
          <span className="font-medium">Allow</span>
        </ChoiceRow>
        {canAlwaysAllow ? (
          <ChoiceRow
            index={1}
            highlighted={!rejecting && highlighted === 1}
            ariaLabel={`Always allow ${grant.label}`}
            onHover={() => setHighlighted(1)}
            onSelect={() => choose("always")}
          >
            <span className="font-medium shrink-0">Always allow</span>
            <NoxusTooltip
              asChild
              content={`Genie stops asking before ${grant.label.toLowerCase()}. Everything else still asks.`}
            >
              <span className="flex min-w-0">
                <Badge variant={grant.variant} ellipsis>
                  {grant.icon}
                  <span data-label>{grant.label}</span>
                </Badge>
              </span>
            </NoxusTooltip>
          </ChoiceRow>
        ) : (
          <ChoiceRow
            index={1}
            highlighted={false}
            disabled
            ariaLabel="Always allow is off"
            onHover={() => {}}
            onSelect={() => {}}
          >
            <span className="flex min-w-0 flex-col">
              <span className="font-medium">Always allow is off</span>
              <span className="text-xs text-muted-foreground">
                Genie asks before each call of this kind
              </span>
            </span>
          </ChoiceRow>
        )}
        {rejecting ? (
          <div className="flex items-start gap-2.5 rounded-lg bg-muted px-2.5 py-2">
            <Kbd className="mt-3.5 bg-card" aria-hidden>
              3
            </Kbd>
            <InputGroup className="min-w-0 flex-1 bg-card">
              <InputGroupTextarea
                autoFocus
                aria-label="Rejection message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={onMessageKey}
                placeholder="Tell the agent what to do instead"
              />
              <InputGroupAddon align="block-end">
                <InputGroupButton
                  size="sm"
                  className="-ml-2.5 rounded-lg"
                  aria-label="Back"
                  onClick={() => setRejecting(false)}
                >
                  Back <Kbd>Esc</Kbd>
                </InputGroupButton>
                <InputGroupButton
                  size="sm"
                  className="-mr-2.5 ml-auto rounded-lg"
                  aria-label="Send rejection"
                  onClick={submitRejection}
                >
                  Reject <Kbd>Enter</Kbd>
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
          </div>
        ) : (
          <ChoiceRow
            index={2}
            highlighted={highlighted === 2}
            ariaLabel="Reject"
            onHover={() => setHighlighted(2)}
            onSelect={() => choose("reject")}
          >
            <span className="font-medium">Reject</span>
            <span className="text-muted-foreground truncate">
              and tell the agent what to do instead
            </span>
          </ChoiceRow>
        )}
      </div>
    </div>
  );
}

function ChoiceRow({
  index,
  highlighted,
  disabled = false,
  ariaLabel,
  onHover,
  onSelect,
  children,
}: {
  index: number;
  highlighted: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onHover: () => void;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      aria-keyshortcuts={disabled ? undefined : String(index + 1)}
      data-highlighted={highlighted || undefined}
      onMouseEnter={onHover}
      onFocus={onHover}
      onClick={onSelect}
      className={cn(
        "flex w-full min-w-0 items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-foreground outline-none transition-[color,background-color,box-shadow] focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        highlighted && "bg-muted",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      )}
    >
      <Kbd className={cn(highlighted && "bg-card")} aria-hidden>
        {index + 1}
      </Kbd>
      {children}
    </button>
  );
}
